import { z } from 'zod';

// DEF-2: Lebenszyklus Neu → In Analyse → In Behebung → Retest bereit → Geschlossen (+ Abgelehnt).
export const DEFECT_STATUS = [
  'neu',
  'in_analyse',
  'in_behebung',
  'retest_bereit',
  'geschlossen',
  'abgelehnt',
] as const;
export type DefectStatus = (typeof DEFECT_STATUS)[number];

// Erlaubte Übergänge. Jeder offene Zustand darf abgelehnt werden; retest_bereit setzt beim
// Eintritt den verknüpften Testfall auf Retest (Kaskade, im Router). Aus einem Endzustand ist nur
// ein Reopen nach in_analyse erlaubt (HITL-Korrektur), nicht der volle Sprung.
export const DEFECT_STATUS_TRANSITIONS: Record<DefectStatus, readonly DefectStatus[]> = {
  neu: ['in_analyse', 'abgelehnt'],
  in_analyse: ['in_behebung', 'retest_bereit', 'abgelehnt'],
  in_behebung: ['retest_bereit', 'abgelehnt'],
  retest_bereit: ['geschlossen', 'in_behebung', 'abgelehnt'],
  geschlossen: ['in_analyse'],
  abgelehnt: ['in_analyse'],
};

export function canTransition(from: DefectStatus, to: DefectStatus): boolean {
  return DEFECT_STATUS_TRANSITIONS[from].includes(to);
}

// DEF-3: Severity/Error-Type sind projekt-konfigurierbares Vokabular (Config-Tabellen, Seed = ETO).
export interface SeverityLevel {
  severity_id: string;
  key: string;
  label_de: string;
  label_en: string;
  rank: number; // 0 = schwerste (Blocker)
  color_token: string; // 'blocker' | 'high' | 'medium' | 'low' — Board mappt auf Token-Klassen
  is_default: boolean;
  sort_order: number;
}

export interface ErrorType {
  error_type_id: string;
  key: string;
  label_de: string;
  label_en: string;
  sort_order: number;
}

// DEF-1: Auto-Defect wird serverseitig aus dem Fehler-Lauf gebaut — kein Create-Body vom Client.
// Das Create-Schema deckt nur den (seltenen) manuellen Anlagefall über die UI ab.
export const DefectCreateSchema = z.object({
  title: z.string().min(1),
  description: z.string().nullable().default(null),
  severity_id: z.string().uuid().nullable().default(null),
  error_type_id: z.string().uuid().nullable().default(null),
  test_case_id: z.string().uuid().nullable().default(null),
  // #57: Herkunfts-Plan. Der Dialog belegt ihn vor, „kein Plan" bleibt waehlbar — ein Pflichtfeld
  // widerspraeche der Begruendung von #39 (Fund im Vorbeigehen).
  test_plan_id: z.string().uuid().nullable().default(null),
});

// #208: Defect zu EINEM Schritt, angelegt vom Tester während der Durchführung — ohne den Testfall zu
// buchen. Plan und Testfall stehen im Pfad; der Schritt ist Pflicht (sonst wäre es ein Fehler-Lauf).
export const StepDefectCreateSchema = z.object({
  failed_step_id: z.string().uuid(),
  title: z.string().trim().min(1),
  description: z.string().nullable().default(null),
  severity_id: z.string().uuid().nullable().default(null),
  error_type_id: z.string().uuid().nullable().default(null),
});
export type StepDefectCreate = z.infer<typeof StepDefectCreateSchema>;

// DEF-7: Felder-Edit (Testmanager/Consultant/zugewiesene Bearbeiter). Alle Felder optional (PATCH).
export const DefectPatchSchema = z
  .object({
    title: z.string().min(1),
    description: z.string().nullable(),
    severity_id: z.string().uuid().nullable(),
    error_type_id: z.string().uuid().nullable(),
    assignee_id: z.string().uuid().nullable(),
    // #57: aenderbar, nicht nur angezeigt — sonst waere ein falsch zugeordneter Defect nicht
    // korrigierbar.
    test_plan_id: z.string().uuid().nullable(),
    // #27: Screenshots am bestehenden Defect nachrüsten (Finding 19). Data-URLs wie bei
    // test_runs.screenshots — Auslagerung in Blob-Storage ist ein bekannter Carry-Forward.
    screenshots: z.array(z.string()),
    // #204 T6a: „✓ Übernehmen" bestätigt einen KI-Titel. Nur `false` erlaubt — `true` setzt allein der
    // Hintergrundlauf, ein Client darf keinen Titel als KI-Vorschlag ausgeben.
    title_ai: z.literal(false),
  })
  .partial();

// #91: Sammelaktion "Defects bearbeiten". Obergrenze 1000 = großzügig über dem realen Katalog
// (546), aber ein Riegel gegen unbegrenzte Bodies. Teilerfolg-Semantik: Objekt-Probleme werden
// übersprungen und benannt (Antwort { updated, skipped }); ZIEL-Probleme (ungültiger
// Tester/Severity) betreffen alle Objekte gleich und bleiben Request-Fehler (422) — wie beim
// Einzel-PATCH.
export const DefectBulkUpdateSchema = z
  .object({
    defect_ids: z.array(z.string().uuid()).min(1).max(1000),
    patch: z.object({
      severity_id: z.string().uuid().optional(),
      assignee_id: z.string().uuid().nullable().optional(),
    }),
  })
  .superRefine((v, ctx) => {
    if (Object.keys(v.patch).length === 0)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'patch_empty', path: ['patch'] });
  });

// #87: `comment` ist die Begründung für den Close-Guard (Router: Schließen ohne bestandenen
// Nachtest verlangt sie). Bei allen anderen Übergängen ignoriert der Router das Feld — kein
// stiller Kommentar-Nebeneffekt. min(1) NACH trim, damit Leerraum nicht als Begründung durchgeht.
export const DefectTransitionSchema = z.object({
  to: z.enum(DEFECT_STATUS),
  comment: z.string().trim().min(1).max(2000).optional(),
});

// #194: Behebung bestätigen. „Behoben" braucht keinen Text; „nicht behoben" immer eine Begründung —
// sie wandert als Kommentar in den Verlauf und erklärt dem Bearbeiter, was noch fehlt. min(1) NACH
// trim, damit Leerraum nicht als Begründung durchgeht (wie bei DefectTransitionSchema).
export const FixCheckConfirmSchema = z.discriminatedUnion('fixed', [
  z.object({ fixed: z.literal(true) }),
  z.object({ fixed: z.literal(false), comment: z.string().trim().min(1).max(2000) }),
]);
export type FixCheckConfirm = z.infer<typeof FixCheckConfirmSchema>;

// #194: Eine offene Behebungs-Prüfung für „Meine Tests" (GET /my-defects/fix-checks). `substituted_for`
// gesetzt, wenn der Aufrufer nur als aktive Vertretung des Melders prüft.
export interface FixCheck {
  defect_id: string;
  human_id: string;
  title: string;
  test_plan_id: string;
  test_case_id: string | null;
  test_case_human_id: string | null;
  test_case_title_de: string | null;
  test_case_title_en: string | null;
  reporter_id: string;
  substituted_for: { user_id: string; name: string } | null;
}

// #105: Body für „einen weiteren Testfall als blockiert verknüpfen" (n:m
// defect_affected_test_cases, zusätzlich zum primären defect.test_case_id).
export const DefectAffectedCreateSchema = z.object({ test_case_id: z.string().uuid() });

// #27: Verlaufseintrag. Der Client schickt NUR den Text — `kind` setzt der Server auf 'comment'.
// Sonst könnte ein Client einen Status-Wechsel fälschen, den es nie gab.
export const DefectCommentCreateSchema = z.object({ body: z.string().min(1) });

// #204: `title_ai` = KI hat den Titel vorgeschlagen (body = alter Titel, kein Autor).
export type DefectCommentKind = 'comment' | 'status_change' | 'title_ai';

export interface DefectComment {
  comment_id: string;
  kind: DefectCommentKind;
  body: string | null;
  from_status: DefectStatus | null;
  to_status: DefectStatus | null;
  author_name: string | null;
  created_at: string;
}

// #208: Der Schritt, an dem ein Defect hängt — EINE Form für Tester-Detail, Testmanager-Detail und
// Board-Liste (null ohne Schritt oder nach Löschen des Schritts). Der Input (bei BW-Tests der
// Datenquellen-Name) ist die eigentlich wichtige Angabe.
export interface DefectFailedStep {
  step_no: number;
  description_de: string | null;
  description_en: string | null;
  input_de: string | null;
  input_en: string | null;
}

// #208 Nachtrag: „notierter Fehler dieser Durchführung“ — ein loser Defect (kein Lauf, nicht
// automatisch) zu Plan + Testfall, angelegt nach dem letzten gebuchten Lauf. „Fehler melden“ bietet
// ihn als Grund an. `failed_step` ist null, wenn der Katalog-Schritt inzwischen gelöscht wurde
// (oder der Defect ohne Schritt angelegt wurde) — er bleibt trotzdem notiert.
export interface NotedStepDefect {
  defect_id: string;
  human_id: string;
  title: string;
  failed_step: DefectFailedStep | null;
}
// Sortiert nach `failed_step.step_no` (ohne Schritt zuletzt), dann `human_id`.
export interface NotedStepDefectList {
  defects: NotedStepDefect[];
}

export interface Defect {
  defect_id: string;
  human_id: string;
  title: string;
  // #204: true = der Titel stammt von der KI (Abzeichen); ein von Hand geänderter Titel setzt es zurück.
  title_ai: boolean;
  description: string | null;
  status: DefectStatus;
  severity_id: string | null;
  // #90: true = ein Mensch hat die Severity BEWUSST gesetzt — auch „auf null/ohne gesetzt" ist eine
  // Triage-Entscheidung. Auto-Defect und POST ohne explizite severity_id bleiben false.
  severity_triaged: boolean;
  error_type_id: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  test_plan_id: string | null;
  test_run_id: string | null;
  test_case_id: string | null;
  test_case_human_id: string | null;
  // #188: Titel des primären Testfalls, beide Sprachen; null ohne Testfall. Optional wie stops_chain —
  // POST/PATCH-Antworten tragen es nicht.
  test_case_title_de?: string | null;
  test_case_title_en?: string | null;
  // #194: Name des Melders, auf dessen Bestätigung der Fehler in „Retest bereit" wartet; null/fehlt =
  // keine offene Prüfung. Optional wie stops_chain — nur GET-Detail und GET-Liste berechnen es.
  awaiting_reporter_name?: string | null;
  failed_step_id: string | null;
  // #208: Schritt-Nummer + Input; GET-Detail und die Board-Liste (GET /defects) berechnen es,
  // POST/PATCH-Antworten tragen es nicht (fehlt wie null).
  failed_step?: DefectFailedStep | null;
  tcode: string | null;
  screenshots: string[];
  is_auto: boolean;
  // #126: Antwort des Testers im Fehler-Dialog („Hält der Fehler die E2E-Kette an?"), abgeleitet
  // aus dem Ergebnis des verknüpften Laufs: true = error-Lauf, false = bestandener Lauf mit
  // Defect („bestanden mit Fehler"), null = manueller Defect oder Lauf gelöscht. Nicht änderbar.
  // Optional, weil nur GET /defects/:id es berechnet — POST/PATCH-Antworten tragen es nicht; die
  // Oberfläche behandelt „fehlt" wie null.
  stops_chain?: boolean | null;
  created_at: string;
  updated_at: string;
}

// Board-Liste: schlanker als das Detail (keine Screenshots), plus abgeleiteter Swim-Lane-Knoten.
export interface DefectListEntry {
  defect_id: string;
  human_id: string;
  title: string;
  // #204: siehe Defect.title_ai.
  title_ai: boolean;
  status: DefectStatus;
  severity_id: string | null;
  // #90: siehe Defect.severity_triaged.
  severity_triaged: boolean;
  assignee_id: string | null;
  assignee_name: string | null;
  // #57: test_plan_name liest die Tabellenansicht des Boards (Spalte "Testplan"). test_plan_id
  // hat aktuell keinen Leser — der Plan-Filter arbeitet mit lokalem Komponenten-State
  // (board-page.tsx), nicht mit diesem Feld. Es bleibt bewusst stehen, damit ein Client einen
  // Eintrag seinem Plan zuordnen kann, ohne ueber den Namen zu gehen (Namen sind nicht eindeutig).
  test_plan_id: string | null;
  test_plan_name: string | null;
  test_case_id: string | null;
  test_case_human_id: string | null;
  // #188: Titel des primären Testfalls, beide Sprachen; null ohne Testfall. Optional wie stops_chain —
  // POST/PATCH-Antworten tragen es nicht.
  test_case_title_de?: string | null;
  test_case_title_en?: string | null;
  // #194: siehe Defect.awaiting_reporter_name.
  awaiting_reporter_name?: string | null;
  tcode: string | null;
  // #208: siehe Defect.failed_step.
  failed_step?: DefectFailedStep | null;
  node_id: string | null; // Teilprojekt/Stream für Swim-Lanes (DEF-4)
  node_name: string | null;
  created_at: string;
}

// DEF-6 / AI-F: Cluster-/Dubletten-Hinweis an einem Defect.
export interface ClusterHint {
  defect_id: string;
  human_id: string;
  title: string;
  kind: 'duplicate' | 'related';
  reason: string;
}

export type DefectPatch = z.infer<typeof DefectPatchSchema>;
export type DefectTransition = z.infer<typeof DefectTransitionSchema>;
